import React, { useState } from 'react';

import { Box, Table, TableRow, TableCell, TableBody, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { enUS, es, ja, pt } from 'date-fns/locale';
import Moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import ModalContainer from '../../../ModalContainer';
import ApiError from '../../ClassSessions/ClassSessionPage/ContentTypes/ApiError';

let { language } = window.navigator;

if (language.length > 2) {
    language = language.substr(0, 2);
}

const barHeight = 24;

const languageDateModules = {
    en: {
        locale: enUS,
        formatString: 'H:mm'
    },
    es: {
        locale: es,
        formatString: 'H:mm'
    },
    ja: {
        locale: ja,
        formatString: 'H:mm'
    },
    pt: {
        locale: pt,
        formatString: 'H:mm'
    }
};
const languageDateModule = languageDateModules[language] ?? languageDateModules.en;
const { formatString, locale } = languageDateModule;

const eventColors = {
    IS_TALKING: 'RGB(165, 85, 70)',
    RAISED_HAND: 'RGB(213,135,117)',
    PUBLIC_CHAT: 'purple',
    PRIVATE_CHAT: 'lightpurple',
    FOCUSED: 'RGB(240, 230, 215)',
    ATTENDANCE: 'RGB(230, 180, 155)'
};

const eventValues = {
    IS_TALKING: 'true',
    RAISED_HAND: 'true',
    PUBLIC_CHAT: 'true',
    PRIVATE_CHAT: 'true',
    FOCUSED: 'false',
    ATTENDANCE: 'true'
};

const eventIndexes = {
    IS_TALKING: '6',
    RAISED_HAND: '3',
    PUBLIC_CHAT: '4',
    PRIVATE_CHAT: '5',
    FOCUSED: '2',
    ATTENDANCE: '1',
    FEEDBACK: '7',
    REACTION: '8',
    STARS: '9',
    ERROR: '10'
};

interface UserTimelineClassSessionRowProps {
    actionFilter?: string[];
    classSessionData: any;
    index?: number;
    startShown?: boolean;
}

const UserTimelineClassSessionRow: React.FC<UserTimelineClassSessionRowProps> = (props) => {
    const { t } = useTranslation('portal');
    // const history = useHistory();
    const { actionFilter: startingActionFilter, classSessionData, index: startingIndex, startShown } = props;
    const {
        class: { class_name: className },
        reportingEvents,
        class_session: { date_str: dateStr, start_time: rawStartTime, end_time: rawEndTime }
    } = classSessionData;

    const index = startingIndex || 0;
    const actionFilter = startingActionFilter || [];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isShown, setIsShown] = useState(startShown || false);
    const [isHover, setIsHover] = useState(false);
    const { pushModalContent } = useContainer(ModalContainer);

    const removeShow = () => {
        if (startShown) return;
        pushModalContent(
            <Box
                style={{
                    width: `${window.innerWidth - 200}px`
                }}
            >
                <UserTimelineClassSessionRow
                    classSessionData={classSessionData}
                    startShown={true}
                ></UserTimelineClassSessionRow>
            </Box>
        );
    };

    const classDuration = Number(rawEndTime) - Number(rawStartTime);
    const startTime = Number(rawStartTime) - classDuration * 0.05;
    const endTime = Number(rawEndTime) + classDuration * 0.05;

    const rawStartPercentage =
        100 * Math.min(1, Math.max(0, (Number(rawStartTime) - startTime) / (endTime - startTime)));
    const rawEndPercentage = 100 * Math.min(1, Math.max(0, (Number(rawEndTime) - startTime) / (endTime - startTime)));

    const aggregates = {
        'Attendance Time': 0,
        'Focused Time': 0,
        'Raised Hand Time': 0,
        'Raised Hand Count': 0,
        'Talking Time': 0,
        'Public Chat Count': 0,
        'Private Chat Count': 0,
        'Feedback Count': 0,
        'Reaction Count': 0,
        'Stars Count': 0
    };
    reportingEvents.forEach((reportingEvent) => {
        reportingEvent.duration =
            (Number(reportingEvent.end_timestamp) - Number(reportingEvent.start_timestamp)) / (1000 * 60);
        if (reportingEvent.event_type === 'ATTENDANCE' && reportingEvent.event_value === 'true') {
            aggregates['Attendance Time'] += Number(reportingEvent.duration);
        }
        if (reportingEvent.event_type === 'IS_TALKING' && reportingEvent.event_value === 'true') {
            aggregates['Talking Time'] += Number(reportingEvent.duration);
        }
        if (reportingEvent.event_type === 'FOCUSED' && reportingEvent.event_value === 'true') {
            aggregates['Focused Time'] += Number(reportingEvent.duration);
        }
        if (reportingEvent.event_type === 'RAISED_HAND' && reportingEvent.event_value === 'true') {
            aggregates['Raised Hand Time'] += Number(reportingEvent.duration);
            aggregates['Raised Hand Count'] += 1;
        }
        if (reportingEvent.event_type === 'FEEDBACK' && reportingEvent.event_value !== '') {
            aggregates['Feedback Count'] += 1;
        }
        if (reportingEvent.event_type === 'REACTION' && reportingEvent.event_value !== '') {
            aggregates['Reaction Count'] += 1;
        }
        if (reportingEvent.event_type === 'STARS' && reportingEvent.event_value !== '') {
            aggregates['Stars Count'] += Number(reportingEvent.event_value);
        }
        if (reportingEvent.event_type === 'PUBLIC_CHAT') {
            aggregates['Public Chat Count'] += 1;
        }
        if (reportingEvent.event_type === 'PRIVATE_CHAT') {
            aggregates['Private Chat Count'] += 1;
        }
    });
    return (
        <div key={startingIndex}>
            <div
                style={{
                    width: '100%',
                    position: 'relative',
                    height: isShown ? `${barHeight * Object.keys(eventIndexes).length + 42}px` : `${barHeight}px`,
                    cursor: isHover ? 'pointer' : 'default',
                    // eslint-disable-next-line no-nested-ternary
                    backgroundColor: isShown ? 'rgb(255,255,255)' : index % 2 === 0 ? '#fff' : '#fff',
                    borderTop: index === 0 && !isShown ? '1px solid rgb(220,220,220)' : '',
                    borderBottom: isShown ? '' : '1px solid rgb(220,220,220)',
                    zIndex: isShown ? 2 : 1,
                    marginTop: isShown ? 6 : 0,
                    marginBottom: isShown ? 6 : 0
                }}
                onClick={() => removeShow()}
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
            >
                <div
                    style={{
                        width: '180px',
                        height: isShown ? '32px' : '20px',
                        overflowY: 'hidden',
                        fontSize: isShown ? '16px' : '10px',
                        position: 'relative',
                        padding: '0px',
                        top: isShown ? '-4px' : `${(barHeight - 16) / 2 - 3}px`,
                        fontWeight: isHover || isShown ? 'bold' : 'normal',
                        overflow: 'hidden',
                        lineHeight: '1',
                        marginBottom: isShown ? '10px' : '0px'
                    }}
                >
                    {dateStr}
                    <br />
                    {className}
                </div>
                {isShown ? (
                    <div
                        style={{
                            position: 'absolute',
                            top: isShown ? '-20px' : '10px',
                            left: '200px',
                            fontSize: '10px',
                            backgroundColor: 'rgb(255,255,255)',
                            zIndex: 10,
                            width: 'calc(100% - 200px)'
                        }}
                    >
                        <Table style={{ width: '100%', padding: '0px', marginTop: '0px', borderBottom: '' }}>
                            <TableBody style={{ width: '100%', padding: '0px', margin: '0px', borderBottom: '' }}>
                                <TableRow style={{ width: '100%', padding: '0px', margin: '0px', borderBottom: '' }}>
                                    {Object.keys(aggregates).map((key) => {
                                        const value = aggregates[key];
                                        return (
                                            <TableCell
                                                style={{
                                                    fontSize: '10px',
                                                    padding: '10px',
                                                    margin: '0px',
                                                    borderBottom: 'none',
                                                    lineHeight: '12px'
                                                }}
                                            >
                                                {key}: {Math.round(value * 10) / 10}
                                                {key.indexOf(' Time') !== -1 ? 'min' : ''}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </div>
                ) : (
                    <></>
                )}
                {isShown ? (
                    Object.keys(eventIndexes).map((eventType, key) => {
                        const eventIndex = Number(eventIndexes[eventType]);
                        return (
                            <div
                                style={{
                                    width: '100%',
                                    height: `${barHeight}px`,
                                    fontSize: '14px',
                                    position: 'absolute',
                                    padding: '0px',
                                    top: `${eventIndex * barHeight + 20}px`,
                                    overflow: 'hidden',
                                    lineHeight: '1',
                                    borderBottom: isShown ? '1px solid rgb(220, 220, 220)' : '',
                                    borderTop: eventIndex === 1 ? '1px solid rgb(220, 220, 220)' : ''
                                }}
                                key={key}
                            >
                                <div
                                    style={{
                                        position: 'relative',
                                        top: '4px'
                                    }}
                                >
                                    {eventType}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <></>
                )}
                <div
                    style={{
                        width: 'calc(100% - 200px)',
                        position: 'absolute',
                        top: '0px',
                        height: `${barHeight}px`,
                        left: '200px'
                    }}
                >
                    {reportingEvents.map((reportingEvent: any) => {
                        if (!actionFilter.includes(reportingEvent.event_type) && !isShown) {
                            return;
                        }
                        if (
                            eventValues[reportingEvent.event_type] &&
                            eventValues[reportingEvent.event_type] !== reportingEvent.event_value
                        ) {
                            return;
                        }
                        const startPercentage = Math.min(
                            1,
                            Math.max(
                                0,
                                (Number(reportingEvent.start_timestamp) / 1000 - startTime) / (endTime - startTime)
                            )
                        );
                        const endPercentage = Math.min(
                            1,
                            Math.max(
                                0,
                                (Number(reportingEvent.end_timestamp) / 1000 - startTime) / (endTime - startTime)
                            )
                        );
                        const totalPercentage = endPercentage - startPercentage;
                        if (reportingEvent.event_type === 'PUBLIC_CHAT') {
                            if (reportingEvent.event_value === '' || reportingEvent.event_value === '""') {
                                return <></>;
                            }
                            const emojiUrl = 'https://classedu.class.com/images/chat-icon.png';
                            return (
                                <Tooltip
                                    arrow
                                    title={`${reportingEvent.event_type} ${format(
                                        Number(reportingEvent.start_timestamp),
                                        formatString,
                                        { locale }
                                    )} - ${format(Number(reportingEvent.end_timestamp), formatString, { locale })}`}
                                >
                                    <img
                                        src={emojiUrl}
                                        style={{
                                            height: `${barHeight / 1.5}px`,
                                            left: `calc(${startPercentage * 100}% - 5px)`,
                                            position: 'absolute',
                                            top: isShown
                                                ? `${
                                                      barHeight * Number(eventIndexes[reportingEvent.event_type]) +
                                                      20 +
                                                      4
                                                  }px`
                                                : `${4}px`,
                                            zIndex: 8
                                        }}
                                    />
                                </Tooltip>
                            );
                        }
                        if (reportingEvent.event_type === 'PRIVATE_CHAT') {
                            if (reportingEvent.event_value === '' || reportingEvent.event_value === '""') {
                                return <></>;
                            }
                            const emojiUrl = 'https://classedu.class.com/images/chat-icon.png';
                            return (
                                <Tooltip
                                    arrow
                                    title={`${reportingEvent.event_type} ${format(
                                        Number(reportingEvent.start_timestamp),
                                        formatString,
                                        { locale }
                                    )} - ${format(Number(reportingEvent.end_timestamp), formatString, { locale })}`}
                                >
                                    <img
                                        src={emojiUrl}
                                        style={{
                                            height: `${barHeight / 1.5}px`,
                                            left: `calc(${startPercentage * 100}% - 5px)`,
                                            position: 'absolute',
                                            top: isShown
                                                ? `${
                                                      barHeight * Number(eventIndexes[reportingEvent.event_type]) +
                                                      20 +
                                                      2
                                                  }px`
                                                : `${2}px`,
                                            zIndex: 8
                                        }}
                                    />
                                </Tooltip>
                            );
                        }
                        if (reportingEvent.event_type === 'FEEDBACK') {
                            if (
                                reportingEvent.event_value === 'clear' ||
                                reportingEvent.event_value === '' ||
                                reportingEvent.event_value === '""'
                            ) {
                                return <></>;
                            }
                            const emojiUrl = `https://classedu.class.com/images/emojis/emoji-${reportingEvent.event_value}.png`;
                            return (
                                <Tooltip
                                    arrow
                                    title={`${reportingEvent.event_type} ${format(
                                        Number(reportingEvent.start_timestamp),
                                        formatString,
                                        { locale }
                                    )} - ${format(Number(reportingEvent.end_timestamp), formatString, { locale })}`}
                                >
                                    <img
                                        src={emojiUrl}
                                        style={{
                                            height: `${barHeight / 1.5}px`,
                                            left: `calc(${startPercentage * 100}% - 7px)`,
                                            position: 'absolute',
                                            top: isShown
                                                ? `${
                                                      barHeight * Number(eventIndexes[reportingEvent.event_type]) +
                                                      20 +
                                                      3
                                                  }px`
                                                : `${3}px`,
                                            zIndex: 9
                                        }}
                                    />
                                </Tooltip>
                            );
                        }
                        if (reportingEvent.event_type === 'REACTION') {
                            if (
                                reportingEvent.event_value === 'clear' ||
                                reportingEvent.event_value === '' ||
                                reportingEvent.event_value === '""'
                            ) {
                                return <></>;
                            }
                            const emojiUrl = `https://classedu.class.com/images/emojis/emoji-${reportingEvent.event_value}.png`;
                            return (
                                <Tooltip
                                    arrow
                                    title={`${reportingEvent.event_type} ${format(
                                        Number(reportingEvent.start_timestamp),
                                        formatString,
                                        { locale }
                                    )} - ${format(Number(reportingEvent.end_timestamp), formatString, { locale })}`}
                                >
                                    <img
                                        src={emojiUrl}
                                        style={{
                                            height: `${barHeight / 1.5}px`,
                                            left: `calc(${startPercentage * 100}% - 7px)`,
                                            position: 'absolute',
                                            top: isShown
                                                ? `${
                                                      barHeight * Number(eventIndexes[reportingEvent.event_type]) +
                                                      20 +
                                                      3
                                                  }px`
                                                : `${3}px`,
                                            zIndex: 9
                                        }}
                                    />
                                </Tooltip>
                            );
                        }
                        // 'level' value backwards compatibility support
                        if (
                            reportingEvent.event_type === 'ERROR' &&
                            (!reportingEvent.data.level || reportingEvent.data.level === 'error')
                        ) {
                            const emojiUrl = 'https://classedu.test.class.com/error_icon.png';
                            return (
                                <Tooltip
                                    arrow
                                    title={`${reportingEvent.event_type} ${format(
                                        Number(reportingEvent.start_timestamp),
                                        formatString,
                                        { locale }
                                    )} - ${format(Number(reportingEvent.end_timestamp), formatString, { locale })}`}
                                >
                                    <img
                                        src={emojiUrl}
                                        onMouseDown={() => {
                                            console.log('alertReportingEvent', reportingEvent);
                                            pushModalContent(
                                                <ApiError
                                                    format={'json'}
                                                    key={0}
                                                    item={{ ...reportingEvent, data: reportingEvent }}
                                                />
                                            );
                                            return false;
                                        }}
                                        style={{
                                            height: `${barHeight / 1.5}px`,
                                            left: `calc(${startPercentage * 100}% - 7px)`,
                                            position: 'absolute',
                                            top: isShown
                                                ? `${
                                                      barHeight * Number(eventIndexes[reportingEvent.event_type]) +
                                                      20 +
                                                      4
                                                  }px`
                                                : `${4}px`,
                                            zIndex: 9
                                        }}
                                    />
                                </Tooltip>
                            );
                        }
                        if (reportingEvent.event_type === 'STARS') {
                            if (reportingEvent.event_value === '0') {
                                return <></>;
                            }
                            return (
                                <>
                                    <Tooltip
                                        arrow
                                        title={`${reportingEvent.event_value} ${reportingEvent.event_type} ${format(
                                            Number(reportingEvent.start_timestamp),
                                            formatString,
                                            { locale }
                                        )} - ${format(Number(reportingEvent.end_timestamp), formatString, { locale })}`}
                                    >
                                        <img
                                            src="https://classedu.class.com/images/emojis/emoji-star.png"
                                            style={{
                                                height: `${4 + barHeight / 1.5}px`,
                                                left: `calc(${startPercentage * 100}% - 8px)`,
                                                position: 'absolute',
                                                top: isShown
                                                    ? `${
                                                          barHeight * Number(eventIndexes[reportingEvent.event_type]) +
                                                          20 +
                                                          2
                                                      }px`
                                                    : `${2}px`,
                                                zIndex: 9
                                            }}
                                        />
                                    </Tooltip>
                                </>
                            );
                        }
                        if (!eventColors[reportingEvent.event_type]) {
                            return;
                        }
                        const color = eventColors[reportingEvent.event_type];
                        return (
                            <Tooltip
                                arrow
                                title={`${reportingEvent.event_type} ${format(
                                    Number(reportingEvent.start_timestamp),
                                    formatString,
                                    { locale }
                                )} - ${format(Number(reportingEvent.end_timestamp), formatString, { locale })}`}
                            >
                                <div
                                    style={{
                                        backgroundColor: color,
                                        width: `${totalPercentage * 100}%`,
                                        left: `${startPercentage * 100}%`,
                                        height: `${barHeight - 5}px`,
                                        position: 'absolute',
                                        top: isShown
                                            ? `${
                                                  barHeight * Number(eventIndexes[reportingEvent.event_type]) +
                                                  20 +
                                                  -2 +
                                                  4
                                              }px`
                                            : '2px',
                                        zIndex: eventIndexes[reportingEvent.event_type] || 1
                                    }}
                                ></div>
                            </Tooltip>
                        );
                    })}
                </div>
            </div>
            {isShown && (
                <div
                    style={{
                        height: 'calc(100% - 180px)',
                        width: '100%',
                        top: '65px',
                        marginBottom: '50px'
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            left: '200px',
                            width: 'calc(100% - 200px)',
                            height: 'calc(100% - 180px)',
                            top: '65px'
                        }}
                    >
                        <div
                            id="startLine"
                            style={{
                                left: `${rawStartPercentage}%`,
                                top: '0px',
                                height: '100%',
                                width: '1px',
                                position: 'absolute',
                                borderLeft: '1px dashed #000',
                                zIndex: '5'
                            }}
                        ></div>
                        <div
                            style={{
                                left: `${rawStartPercentage}%`,
                                top: 'calc(100% + 5px)',
                                fontSize: '12px',
                                textAlign: 'center',
                                position: 'absolute',
                                zIndex: '5',
                                transform: 'translateX(-50%)',
                                lineHeight: '12px',
                                width: '100px'
                            }}
                        >
                            {t('session-start')}
                            <br />
                            {Moment(new Date(Number(rawStartTime) * 1000)).format('h:mm A')}
                        </div>
                        <div
                            style={{
                                left: `${rawEndPercentage}%`,
                                top: '0px',
                                height: '100%',
                                width: '1px',
                                position: 'absolute',
                                borderLeft: '1px dashed #000',
                                zIndex: '5'
                            }}
                        ></div>
                        <div
                            style={{
                                left: `${rawEndPercentage}%`,
                                top: 'calc(100% + 5px)',
                                fontSize: '12px',
                                textAlign: 'center',
                                position: 'absolute',
                                zIndex: '5',
                                transform: 'translateX(-50%)',
                                lineHeight: '12px',
                                width: '100px'
                            }}
                        >
                            {t('session-end')}
                            <br />
                            {Moment(new Date(Number(rawEndTime) * 1000)).format('h:mm A')}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserTimelineClassSessionRow;
